import { ChangeEvent, FormEvent, useLayoutEffect, useState } from "react";
import styled from "styled-components";

import { MINI_FORM_TABLE_FILE_UPLOAD_MODAL_SELECTION_OPTIONS } from "features/product/constants/form";
import { SubProductAction } from "features/product/types/actions.subproduct";
import BasicInput from "features/shared/components/form/components/basicInput";
import SelectInput from "features/shared/components/form/components/selectInput";
import SubmitButton from "features/shared/components/form/components/submitButton";
import ModalHeader from "features/shared/components/modals/modalHeader";
import Modal from "features/shared/components/modal";

import { useDispatch, useSelector } from "services/redux/rootReducer";
import uploadFile from "services/firebase/fileUpload";
import deleteFileWithURL from "services/firebase/deleteFileWithURL";
import LabelWrapper from "features/shared/components/form/components/labelWrapper";
import { BiImage } from "react-icons/bi";
import {
    Container,
    FormContainer,
    ModalContainer,
} from "./subProductEditModal/subProductEditModal.styled";
import useCurrentTableState from "../hooks/useCurrentTableState";
import { IFileUploadModal } from "features/product/types/modals";

interface Props {
    onCloseHandler: () => void;
}

const FileUploadModal: React.FC<Props & IFileUploadModal["params"]> = ({
    onCloseHandler,
    subProductId,
    propertyAccessor,
    isImageFile,
}) => {
    const [inputType, setInputType] = useState<"link" | "file">("link");
    const [uploadedInfo, setUploadedInfo] = useState<string>("");
    const [file, setFile] = useState<File | null>();
    const [imagePreviewLink, setImagePreviewLink] = useState<string>("");
    const [isFileUploading, setFileUploading] = useState<boolean>(false);
    const [isLinkStorage, setLinkStorage] = useState<boolean>(false);

    const dispatch = useDispatch();
    const { readOnlyMode } = useSelector((state) => state.subProducts);

    const { subProducts } = useCurrentTableState();

    useLayoutEffect(() => {
        setUploadedInfo(
            subProducts[subProductId].properties[propertyAccessor]
                .value as string
        );
    }, [subProducts, subProductId, propertyAccessor]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (inputType === "file") {
            // Upload process for files -> turns to process for link
            if (!file) return alert(`You haven't uploaded a file`);
            if (!window.confirm("Are you sure you want to upload this file?"))
                return;
            setFileUploading(true);
            setUploadedInfo(await uploadFile(file));
            setFileUploading(false);
            setInputType("link");
            setLinkStorage(true);

            alert("File upload done. Your file has been converted into a url.");

            return;
        }

        const previousUrl = subProducts[subProductId].properties[
            propertyAccessor
        ].value as string;
        if (
            previousUrl && // previous url is not empty
            uploadedInfo !== previousUrl && // previous url is not the same as the one we just uploaded
            previousUrl.startsWith("https://firebasestorage.googleapis.com") // previous url is a google cloud storage url
        ) {
            await handleDeleteFile(previousUrl);
        }

        dispatch({
            type: SubProductAction.SET_SUBPRODUCT_PROPERTY_VALUE,
            subProductId,
            accessor: propertyAccessor,
            propertyValue: uploadedInfo,
        });

        // if the subproduct doesn't have an image yet we set the image to the uploaded file
        if (
            isImageFile &&
            (!subProducts[subProductId].image ||
                subProducts[subProductId].image === previousUrl)
        ) {
            dispatch({
                type: SubProductAction.SET_SUBPRODUCT_IMAGE_VALUE,
                subProductId,
                imageUrl: uploadedInfo ?? "",
            });
        }

        onCloseHandler();
    };

    const handleDeleteFile = async (link: string) => {
        setFileUploading(true);
        if (isImageFile) setImagePreviewLink("");
        try {
            await deleteFileWithURL(link);
        } catch {
            // don't do anything
        }
        setFileUploading(false);
    };

    const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (isLinkStorage) {
            if (
                window.confirm(
                    "Are you sure you want to delete the previously uploaded file?"
                )
            ) {
                await handleDeleteFile(uploadedInfo);
                setUploadedInfo("");
            }
            return;
        }

        setUploadedInfo(e.target.value);
    };

    const handleInputTypeChange = (value: "link" | "file") => {
        if (isImageFile) setImagePreviewLink("");
        setInputType(value);
    };

    const handleFileSubmit = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target?.files?.length) {
            const file = e.target.files[0];
            setFile(file);
            if (isImageFile) {
                setImagePreviewLink(URL.createObjectURL(file));
                setUploadedInfo("");
            }
        }
    };
    return (
        <Modal id="subProductFileUpload" onCloseHandler={onCloseHandler}>
            {!readOnlyMode || !isImageFile ? (
                <ModalContainer>
                    <ModalHeader
                        onCloseHandler={onCloseHandler}
                        headingText={`${readOnlyMode ? "View" : "Upload"} ${
                            isImageFile ? "Image" : ""
                        } File or URL`}
                    />

                    <FormContainer onSubmit={handleSubmit}>
                        <Container>
                            <LabelWrapper label="Input Type">
                                <SelectInput
                                    value={inputType}
                                    selectionOptions={
                                        MINI_FORM_TABLE_FILE_UPLOAD_MODAL_SELECTION_OPTIONS
                                    }
                                    readOnlyMode={readOnlyMode}
                                    onSelectHandler={handleInputTypeChange}
                                />
                            </LabelWrapper>
                            {inputType === "link" ? (
                                <LinkInputContainer>
                                    <LabelWrapper label="Current Link or URL">
                                        <BasicInput
                                            name="link"
                                            onChangeHandler={handleChange}
                                            value={uploadedInfo}
                                            disabled={readOnlyMode}
                                        />
                                    </LabelWrapper>
                                </LinkInputContainer>
                            ) : (
                                <FileUploadInputContainer>
                                    <input
                                        type="file"
                                        onChange={handleFileSubmit}
                                    />
                                </FileUploadInputContainer>
                            )}
                        </Container>
                        {!readOnlyMode && (
                            <SubmitButton
                                text={
                                    inputType === "link"
                                        ? isFileUploading
                                            ? "Processing..."
                                            : "Confirm"
                                        : isFileUploading
                                        ? "Uploading..."
                                        : "Upload"
                                }
                                disabled={isFileUploading}
                            />
                        )}
                    </FormContainer>
                </ModalContainer>
            ) : null}
            {isImageFile ? (
                <ModalContainer style={{ marginLeft: "1rem" }}>
                    <ModalHeader
                        onCloseHandler={onCloseHandler}
                        headingText={`Image Preview`}
                    />
                    <ImageContainer>
                        {uploadedInfo || imagePreviewLink ? (
                            <img
                                src={uploadedInfo || imagePreviewLink}
                                alt=""
                            />
                        ) : (
                            <BiImage />
                        )}
                    </ImageContainer>
                </ModalContainer>
            ) : null}
        </Modal>
    );
};

const ImageContainer = styled(Container)`
    display: grid;
    place-items: center;
    padding: 1rem;

    & > * {
        font-size: 4rem;
        color: ${(props) => props.theme.colors.neutral[200]};
        height: 100%;
        width: 100%;
        max-height: 100%;
        max-width: 100%;
        object-fit: cover;
    }
`;

const LinkInputContainer = styled.div`
    display: flex;
    flex-flow: column;

    & > * {
        display: contents;
        & > *:last-child {
            width: 100%;
        }
    }
`;

const FileUploadInputContainer = styled.div`
    display: flex;
    flex-flow: column;
`;

export default FileUploadModal;
